import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { ScrollArea } from '../../../components/ui/scroll-area';
import type { CommitteePaperItem } from '@/hooks/use-committee-papers';
import { useAssignEvaluators, useAvailableEvaluators } from '@/hooks/use-committee-papers';

interface AssignEvaluatorsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  paper: CommitteePaperItem | null;
}

export function AssignEvaluatorsDialog({ open, onOpenChange, eventId, paper }: AssignEvaluatorsDialogProps) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { data: evaluators = [], isLoading } = useAvailableEvaluators(eventId, paper?.id || '', open);
  const assignMutation = useAssignEvaluators(eventId);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelectedIds([]);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return evaluators;
    return evaluators.filter((item) =>
      `${item.name} ${item.email} ${item.institution || ''}`.toLowerCase().includes(q),
    );
  }, [evaluators, search]);

  const selectedCount = selectedIds.length;
  const percent = Math.round((selectedCount / 3) * 100);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      if (prev.length >= 3) {
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleSubmit = async () => {
    if (!paper) return;
    if (selectedIds.length === 0 || selectedIds.length > 3) {
      toast.error('Debe seleccionar entre 1 y 3 evaluadores.');
      return;
    }

    try {
      await assignMutation.mutateAsync({
        paperId: paper.id,
        evaluatorIds: selectedIds,
      });
      toast.success('Evaluadores asignados correctamente.');
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'No se pudo asignar evaluadores.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-[#1e3a5f]">Asignar Evaluadores</DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            {paper ? `${paper.folio} - ${paper.title}` : 'Seleccione evaluadores activos para esta ponencia.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">Seleccione hasta 3 evaluadores activos para esta ponencia.</p>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="pl-9"
              placeholder="Buscar evaluador..."
            />
          </div>

          <ScrollArea className="max-h-[320px] rounded-lg border border-gray-200">
            <div className="space-y-2 p-2">
              {isLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cargando evaluadores...
                </div>
              ) : filtered.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500">No hay evaluadores disponibles.</p>
              ) : (
                filtered.map((item) => {
                  const checked = selectedIds.includes(item.id);
                  const reachedMax = selectedIds.length >= 3 && !checked;
                  return (
                    <button
                      type="button"
                      key={item.id}
                      className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition ${
                        checked ? 'border-[#c9a227] bg-yellow-50' : 'border-gray-200 hover:border-gray-300'
                      } ${reachedMax ? 'opacity-60' : ''}`}
                      onClick={() => !reachedMax && toggle(item.id)}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => !reachedMax && toggle(item.id)} />
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-700">
                        {item.name
                          .split(' ')
                          .slice(0, 2)
                          .map((part) => part.charAt(0).toUpperCase())
                          .join('')}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-slate-900">{item.name}</p>
                        <p className="truncate text-xs text-slate-600">{item.email}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant="outline" className="text-xs">
                          {item.currentLoad} asignaciones
                        </Badge>
                        {item.isAtMaxLoad ? (
                          <Badge className="border border-orange-400 bg-orange-50 text-orange-700 hover:bg-orange-50">
                            Carga maxima
                          </Badge>
                        ) : (
                          <span className="h-2 w-2 rounded-full bg-green-500" />
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </ScrollArea>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <p className="font-medium text-gray-700">{selectedCount} de 3 seleccionados</p>
              <p className="text-gray-500">{percent}%</p>
            </div>
            <div className="h-2 rounded-full bg-yellow-100">
              <div
                className="h-2 rounded-full bg-[#c9a227] transition-all"
                style={{ width: `${Math.min(100, percent)}%` }}
              />
            </div>
          </div>

          {selectedCount > 3 ? (
            <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700">
              <AlertCircle className="h-4 w-4" />
              Solo se permiten 3 evaluadores.
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={assignMutation.isPending}>
            Cancelar
          </Button>
          <Button
            className="bg-[#c9a227] text-white hover:bg-[#b8921f]"
            onClick={handleSubmit}
            disabled={assignMutation.isPending || selectedCount === 0 || selectedCount > 3}
          >
            {assignMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <X className="mr-2 h-4 w-4" />}
            Confirmar Asignacion
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
